/**
 * VendorSalesAnalytics — daily / weekly / monthly sales overview for ANY vendor
 * dashboard (restaurant, retail/wholesale, healthcare). Summary cards (today /
 * this week / this month) + a revenue trend chart with a Day/Week/Month toggle +
 * payment split, top items, and a delivered-vs-cancelled tally. Revenue is
 * delivered-orders-only (matches the backend). Orders link to the vendor by
 * orderItems.vendorId / restaurantId, so one component serves every vendor type.
 *
 * Self-contained: `<VendorSalesAnalytics vendorId={id} />` (optional `title`).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import io from "socket.io-client";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { apiFetchVendorSalesAnalytics } from "../api/index";
import { attachVendorOrderRooms } from "../hooks/useVendorOrderRooms";
import { useBranchScope } from "../context/BranchScopeContext";
import { getTokenFromLocalStorage } from "../utilities/tokenUtils.js";

// Socket events that mean "this vendor's sales may have changed" → refetch.
// Reused for every vendor type (restaurant / retail / healthcare).
const SALES_EVENTS = [
  "newRestaurantOrder", "newHealthcareOrder", "newOrder",
  "restaurantDashboardUpdate", "healthcareDashboardUpdate",
  "restaurantOrderStatusChanged", "orderStatusChanged", "orderStatusUpdate",
];

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const NAVY = "#0b1240";
const TEAL = "#136274";
const fmtUGX = (n) => "UGX " + Math.round(Number(n) || 0).toLocaleString();
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(v % 1_000 ? 1 : 0) + "k";
  return String(v);
};

const RANGES = [
  { id: "day", label: "Daily", sub: "Last 14 days" },
  { id: "week", label: "Weekly", sub: "Last 12 weeks" },
  { id: "month", label: "Monthly", sub: "Last 12 months" },
];

const PAY_LABEL = { COD: "Cash", MOMO: "MTN MoMo", MTN: "MTN MoMo", AIRTEL: "Airtel", CARD: "Card", VISA: "Card", OTHER: "Other" };

const S = {
  // No horizontal margin. It used to carry `margin: "0 28px 24px"`, which
  // stacked on top of the dashboard Container's own 16px padding and left this
  // block 56px narrower than every sibling card — visibly inset on both sides
  // at phone widths. The sibling that gets it right (ChatAnalytics, rendered
  // directly below this one) sets only a top margin. Inner padding drops to
  // 16px under 600px so the extra width goes to the content, not the gutter.
  wrap: { background: "#fff", borderRadius: 16, boxShadow: "0 6px 22px rgba(15,23,42,0.07)", padding: "22px 24px", margin: "0 0 24px" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, fontSize: "1.15rem", color: "#0f172a", margin: 0 },
  toggle: { display: "inline-flex", background: "#f1f4f8", borderRadius: 10, padding: 3, gap: 2 },
  tBtn: (on) => ({ border: "none", background: on ? "#fff" : "transparent", color: on ? NAVY : "#64748b", fontWeight: 600, fontSize: 13, padding: "7px 14px", borderRadius: 8, cursor: "pointer", boxShadow: on ? "0 1px 4px rgba(15,23,42,.12)" : "none" }),
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 },
  card: (accent) => ({ background: "#f8fafc", border: "1px solid #eef1f6", borderLeft: `4px solid ${accent}`, borderRadius: 12, padding: "14px 16px" }),
  cardLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#90a0b7", marginBottom: 6 },
  cardValue: { fontSize: "1.5rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 },
  cardMeta: { fontSize: 12.5, color: "#6b7a99", marginTop: 6 },
  chartBox: { height: 240, marginBottom: 6 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 18 },
  panel: { border: "1px solid #eef1f6", borderRadius: 12, padding: "14px 16px" },
  panelTitle: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "#90a0b7", marginBottom: 10 },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid #f4f6fb", fontSize: 13.5 },
  muted: { color: "#8a97ad", fontSize: 13 },
};

export default function VendorSalesAnalytics({ vendorId, title = "Sales Analytics" }) {
  // The branch picked in the navbar (4 Oct 2026); none = every location this
  // login may see. The view remounts when it changes.
  const { outletIdParam } = useBranchScope();
  const [range, setRange] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  // Single fetch used by both the range effect and the live socket refresh.
  // `silent` refreshes update the chart in place without the loading spinner or
  // clobbering the view with an error on a transient blip.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!vendorId) { setLoading(false); return; }
    if (!silent) { setLoading(true); setError(null); }
    try {
      const res = await apiFetchVendorSalesAnalytics(vendorId, rangeRef.current, { outletId: outletIdParam });
      setData(res.data);
      setError(null);
    } catch (e) {
      if (!silent) setError(e?.response?.data?.error || "Couldn't load sales analytics.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [vendorId, outletIdParam]);

  // (Re)load on vendor or range change.
  useEffect(() => { load(); }, [load, range]);

  // ── Live refresh: refetch (debounced) when this vendor's orders change ────────
  useEffect(() => {
    if (!vendorId) return undefined;
    const url = process.env.REACT_APP_SERVER_URL;
    if (!url) return undefined;
    const token = getTokenFromLocalStorage && getTokenFromLocalStorage();
    const socket = io(url, token ? { auth: { token } } : undefined);
    // The generic `join` never admitted these rooms, so this refresh was dead.
    // The one vendor join lets the server pick the rooms (4 Oct 2026).
    const stopRooms = attachVendorOrderRooms(socket, vendorId);

    let timer = null;
    const bump = () => { clearTimeout(timer); timer = setTimeout(() => load({ silent: true }), 1500); };
    SALES_EVENTS.forEach((ev) => socket.on(ev, bump));

    return () => { clearTimeout(timer); stopRooms(); socket.off(); socket.disconnect(); };
  }, [vendorId, load]);

  const chart = useMemo(() => {
    const t = data?.trend || [];
    return {
      data: {
        labels: t.map((x) => x.label),
        datasets: [{ label: "Revenue", data: t.map((x) => x.revenue), backgroundColor: TEAL, hoverBackgroundColor: NAVY, borderRadius: 6, maxBarThickness: 34 }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => items[0]?.label,
              label: (ctx) => `${fmtUGX(ctx.parsed.y)} · ${t[ctx.dataIndex]?.orders || 0} order(s)`,
            },
          },
        },
        scales: {
          y: { beginAtZero: true, ticks: { callback: (v) => fmtShort(v), color: "#8a97ad", font: { size: 11 } }, grid: { color: "#f1f4f8" } },
          x: { ticks: { color: "#8a97ad", font: { size: 11 }, maxRotation: 0, autoSkip: true }, grid: { display: false } },
        },
      },
    };
  }, [data]);

  const totalPayRevenue = (data?.payments || []).reduce((s, p) => s + p.revenue, 0) || 1;

  return (
    <div className="vsa-wrap" style={S.wrap}>
      {/* Inline styles can't carry a media query; this is the one place the
          block needs to respond to width. */}
      <style>{`
        @media (max-width: 600px) {
          .vsa-wrap { padding: 18px 16px !important; }
        }
      `}</style>
      <div style={S.head}>
        <h4 style={S.title}><i className="fas fa-chart-line" style={{ color: TEAL }} />{title}</h4>
        <div style={S.toggle} role="tablist" aria-label="Time range">
          {RANGES.map((r) => (
            <button key={r.id} type="button" style={S.tBtn(range === r.id)} onClick={() => setRange(r.id)} aria-pressed={range === r.id}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div style={{ padding: "40px 0", textAlign: "center", color: "#8a97ad" }}>Loading sales…</div>}
      {!loading && error && <div className="alert alert-warning mb-0">{error}</div>}

      {!loading && !error && data && (
        <>
          {/* Summary cards */}
          <div style={S.cards}>
            {[
              { key: "today", label: "Today", accent: "#f5327a" },
              { key: "week", label: "This week", accent: TEAL },
              { key: "month", label: "This month", accent: NAVY },
            ].map(({ key, label, accent }) => {
              const s = data.summary?.[key] || { revenue: 0, orders: 0, aov: 0 };
              return (
                <div key={key} style={S.card(accent)}>
                  <div style={S.cardLabel}>{label}</div>
                  <div style={S.cardValue}>{fmtUGX(s.revenue)}</div>
                  <div style={S.cardMeta}>{s.orders} order{s.orders === 1 ? "" : "s"} · avg {fmtUGX(s.aov)}</div>
                </div>
              );
            })}
          </div>

          {/* Trend chart */}
          <div style={S.panelTitle}>Revenue — {RANGES.find((r) => r.id === range)?.sub} (delivered orders)</div>
          <div style={S.chartBox}>
            <Bar data={chart.data} options={chart.options} />
          </div>

          {/* Payment split · Top items · Fulfilment */}
          <div style={S.grid3}>
            <div style={S.panel}>
              <div style={S.panelTitle}>Payment methods</div>
              {(data.payments || []).length === 0 ? <div style={S.muted}>No delivered orders yet.</div> : data.payments.map((p) => (
                <div key={p.method} style={S.row}>
                  <span style={{ fontWeight: 600, color: "#334155" }}>{PAY_LABEL[p.method] || p.method}</span>
                  <span style={{ color: "#64748b" }}>{p.orders} · {Math.round((p.revenue / totalPayRevenue) * 100)}%</span>
                </div>
              ))}
            </div>

            <div style={S.panel}>
              <div style={S.panelTitle}>Top-selling items</div>
              {(data.topItems || []).length === 0 ? <div style={S.muted}>No sales in this period.</div> : data.topItems.map((it, i) => (
                <div key={it.name + i} style={S.row}>
                  <span style={{ fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{i + 1}. {it.name}</span>
                  <span style={{ color: "#64748b" }}>×{it.qty} · {fmtUGX(it.revenue)}</span>
                </div>
              ))}
            </div>

            <div style={S.panel}>
              <div style={S.panelTitle}>Fulfilment</div>
              {(() => {
                const f = data.fulfilment || { delivered: 0, rejected: 0, inProgress: 0, total: 0 };
                const rate = f.total ? Math.round((f.delivered / f.total) * 100) : 0;
                const line = (label, val, color) => (
                  <div style={S.row}><span style={{ fontWeight: 600, color: "#334155" }}>{label}</span><span style={{ color, fontWeight: 700 }}>{val}</span></div>
                );
                return (
                  <>
                    {line("Delivered", f.delivered, "#0f9d58")}
                    {line("In progress", f.inProgress, "#e6a100")}
                    {line("Cancelled / rejected", f.rejected, "#d64545")}
                    <div style={{ ...S.row, borderBottom: "none" }}><span style={{ fontWeight: 600, color: "#334155" }}>Fulfilment rate</span><span style={{ color: NAVY, fontWeight: 800 }}>{rate}%</span></div>
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
